import { useState } from 'react';
import { Avatar, Box, Typography, useTheme, styled, Skeleton } from '@mui/material';
import Button from 'components/Common/Button';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { useGetAgent, useUpdateAgent } from 'apis/business/agent/hook';
import { ScrollToTop } from 'App';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AgentConfigSkeleton from './components/AgentConfigSkeleton';
import { agentConfigSchema } from '@/utils/schema/validator/AgentValidation';
import Input from 'components/Common/Input';
import Grid from '@mui/material/Grid2';
import Select from 'components/Common/Select';
import {
  useGetBusinessLanguageAccents,
  useGetBusinessVoice,
  useGetBusinessVoices
} from 'apis/business/agent/voice/hook';
import Switch from 'components/Common/Switch';
import { useGetAgentLanguages } from 'apis/business/agent/languages/hook';
import VoiceLibrary from 'components/Common/VoiceLibrary';
import CloneVoiceSelector from 'components/Common/CloneVoice';
import Radio from 'components/Common/Radio';
import MultiPromptImg from '@/assets/MultiPrompt.png';
import { useGetBusinessProfile } from '@/apis/business/hook';
import { useGetCurrentUser } from '@/apis/user/hook';

export default function Identity() {
  const location = useLocation();

  const getTabIndexFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const tabIndex = params.get('tab');
    return tabIndex ? Number(tabIndex) : 0;
  };

  const theme = useTheme();
  const [skipped, setSkipped] = useState(new Set<number>());
  const [value, setValue] = useState<number>(getTabIndexFromUrl);

  const { data: agent, isLoading: isLoadingAgentLoading } = useGetAgent();

  const { mutateAsync: updateAgent, isPending } = useUpdateAgent();

  const methods = useForm({
    values: { agent },
    mode: 'onChange',
    resolver: zodResolver(agentConfigSchema)
  });

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const handleNext = async () => {
    const result = await methods.trigger();
    if (!result) return;

    let newSkipped = skipped;
    if (isStepSkipped(value)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(value);
    }
    const data = methods.getValues();
    await updateAgent(data);

    setValue((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const onSubmit = (data: any) => {
    updateAgent(data);
  };

  const isLoading = isLoadingAgentLoading;

  if (isLoading) {
    return <AgentConfigSkeleton />;
  }

  return (
    <Box>
      <ScrollToTop state={value} />
      <Box bgcolor={theme.palette.background.default} pb={0.5}>
        <Typography variant='h6' sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
          Identity
        </Typography>
        <Typography color='secondary' variant='body2' fontSize={16} mt={0.5} sx={{ fontSize: { xs: '14px', lg: 16 } }}>
          Set up your customer service agent through identity configuration.
        </Typography>
      </Box>

      <FormProvider {...methods}>
        <Box component='form' onSubmit={methods.handleSubmit(onSubmit)} mt={3}>
          <AgentConfigContent />
        </Box>
        <Box display='flex' justifyContent='end' mt={2}>
          <Button
            onClick={value === 2 ? methods.handleSubmit(onSubmit) : handleNext}
            isPending={isPending}
            sx={{ width: { xs: '100%', lg: 'auto' } }}
          >
            Save
          </Button>
        </Box>
      </FormProvider>
    </Box>
  );
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});

function AgentConfigContent() {
  const theme = useTheme();
  const { data: businessProfile } = useGetBusinessProfile();
  const { data: currentUser } = useGetCurrentUser();
  const { data: agent } = useGetAgent();
  const isNotAgencyUser = !['agency_admin', 'agency_admin_readonly', 'agent_builder'].includes(currentUser?.role ?? '');
  const canEditBehaviour = !isNotAgencyUser || Boolean(businessProfile?.behaviour_editable);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(agent?.avatar_url ? agent?.avatar_url : null);
  const navigate = useNavigate();
  const { locationId, agentId } = useParams();

  const { control, watch, setValue } = useFormContext();
  const isWelcomeMessageEnabled = watch('agent.enable_welcome_message');
  const selectedLanguages = watch('agent.languages');
  const selectedVoiceType = watch('agent.voice_type');

  const { data: voices } = useGetBusinessVoices({ voice_type: 'clone' });
  const { data: languagesData, isLoading: isLanguagesLoading } = useGetAgentLanguages();

  const { mutateAsync: updateAgent } = useUpdateAgent();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const newUrl = URL.createObjectURL(file);
      setAvatarUrl(newUrl);
      const formData = new FormData();
      formData.append('agent[avatar]', file);
      await updateAgent(formData);
    }
  };

  return (
    <Box
      bgcolor={theme.palette.background.paper}
      sx={{ border: '1px solid #F3F3F3' }}
      padding={{ xs: 2, lg: '24px' }}
      borderRadius={2}
    >
      <Typography variant='h6' sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
        Agent Avatar
      </Typography>
      <Typography marginTop='6px' color='secondary' mt={0.5} sx={{ fontSize: { xs: '12px', lg: '14px' } }}>
        Upload an Avatar for Your Agent
      </Typography>

      <Box
        sx={{
          gap: { xs: 1, lg: '16px' },
          alignItems: 'center',
          marginTop: { xs: 2, lg: '16px' },
          border: '1px solid #F3F3F3',
          borderRadius: '8px',
          padding: { xs: 1.5, lg: '16px' }
        }}
        display={{ xs: 'block', sm: 'flex' }}
      >
        <Avatar
          src={avatarUrl || '/path/to/default/avatar.png'}
          alt={agent?.name ?? 'User Avatar'}
          sx={{ width: { xs: 56, lg: 64 }, height: { xs: 56, lg: 64 }, mb: { xs: 1, lg: 0 } }}
        />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              component='label'
              role={undefined}
              color='secondary'
              variant='outlined'
              tabIndex={-1}
              sx={{ width: { xs: '100%', lg: 'auto' } }}
            >
              Choose files
              <VisuallyHiddenInput type='file' accept='image/*' onChange={handleFileChange} multiple />
            </Button>
          </Box>
          <Typography
            color='secondary'
            fontSize='12px'
            sx={{ fontSize: { xs: '11px', lg: '12px' }, mt: { xs: 0.5, lg: 0 } }}
          >
            Image size should be 350×180px and under 2.5MB.
          </Typography>
        </Box>
      </Box>
      <Box mt={{ xs: 2, lg: 3 }} border='1px solid #F3F3F3' p={{ xs: 1.5, lg: 2 }} borderRadius='8px'>
        <Grid container spacing={{ xs: 2, lg: '24px' }}>
          <Grid size={{ xs: 12 }}>
            <Input name='agent.name' label='Name' control={control} fullWidth required />
            <Typography
              color='secondary'
              fontSize={12}
              sx={{ fontSize: { xs: '11px', lg: 12 }, mt: { xs: 0.5, lg: 0 } }}
            >
              What’s your agent called on calls?
            </Typography>
          </Grid>
          {isLanguagesLoading ? (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box>
                <Skeleton variant='rounded' height={56} sx={{ borderRadius: 1 }} />

                <Skeleton variant='text' width='80%' height={20} sx={{ mt: 1 }} />
              </Box>
            </Grid>
          ) : (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Select
                label='Language'
                key={String(agent?.language_support)}
                disabled={isLanguagesLoading}
                name={agent?.language_support === 'monolingual' ? 'agent.languages[0]' : 'agent.languages'}
                control={control}
                options={languagesData?.languages || []}
                multiple={agent?.language_support !== 'monolingual'}
                selectionLimit={agent?.language_support === 'bilingual' ? 2 : 999}
                immutableOptions={agent?.language_support === 'monolingual' ? [] : ['en', 'en-IN']}
                required
              />
              <Typography
                color='secondary'
                lineHeight={1.5}
                fontSize={12}
                sx={{ fontSize: { xs: '11px', lg: 12 }, mt: { xs: 0.5, lg: 0 } }}
              >
                {agent?.language_description}
              </Typography>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Select
              label='Tone'
              name='agent.tone'
              control={control}
              options={[
                { value: 'professional', label: 'Professional' },
                { value: 'conversational', label: 'Conversational' },
                { value: 'humorous', label: 'Humorous' },
                { value: 'empathic', label: 'Empathic' },
                { value: 'simple', label: 'Simple' },
                { value: 'academic', label: 'Academic' },
                { value: 'creative', label: 'Creative' },
                { value: 'friendly', label: 'Friendly' }
              ]}
              required
            />
            <Typography
              color='secondary'
              lineHeight={1.5}
              fontSize={12}
              sx={{ fontSize: { xs: '11px', lg: 12 }, mt: { xs: 0.5, lg: 0 } }}
            >
              Choose how your agent should sound
            </Typography>
          </Grid>
        </Grid>
        <Typography
          fontWeight='500'
          marginTop={{ xs: 2, lg: '24px' }}
          marginBottom='0px'
          sx={{ fontSize: { xs: '1rem', lg: '1.125rem' } }}
        >
          Select Voice
        </Typography>
        {agent?.clone_voice_available && (
          <Radio
            label=''
            name='agent.voice_type'
            control={control}
            options={[
              { label: 'Voice Library', value: 'voice_library' },
              { label: 'Clone Voice', value: 'clone_voice' }
            ]}
            onChange={(val) => {
              setValue('agent.voice_id', agent?.voice_type === val?.target?.value ? agent?.voice_id : '');
            }}
          />
        )}
        {selectedVoiceType === 'clone_voice' ? (
          <Box marginTop='4px'>
            <CloneVoiceSelector control={control} name='agent.voice_id' cloneVoices={voices?.data ?? []} />
          </Box>
        ) : (
          <VoiceLibrary
            prefilledId={agent?.voice_id}
            voicesHook={useGetBusinessVoices}
            voiceHook={useGetBusinessVoice}
            languageAccentHook={useGetBusinessLanguageAccents}
            parent='agent'
            control={control}
            languages={selectedLanguages}
          />
        )}
      </Box>
      <Box my={{ xs: 2, lg: 3 }} p={{ xs: 1.5, lg: 2 }} borderRadius={2} border='1px solid #F3F3F3'>
        <Box
          display={{ xs: 'block', lg: 'flex' }}
          justifyContent='space-between'
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          gap={{ xs: 2, lg: '74px' }}
          flexDirection={{ xs: 'column', lg: 'row' }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant='h6' sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
              Welcome Message
            </Typography>
            <Typography color='secondary' mt={1} sx={{ fontSize: { xs: '12px', lg: '14px' } }}>
              By default, our voice assistant will automatically greet callers and start the conversation with a welcome
              message, ensuring a smooth and professional experience.
            </Typography>
          </Box>
          <Box sx={{ flexShrink: 0, mt: { xs: 1, lg: 0 } }}>
            <Switch name='agent.enable_welcome_message' control={control} />
          </Box>
        </Box>
        {isWelcomeMessageEnabled && (
          <Input
            name='agent.welcome_message'
            label=''
            control={control}
            placeholder='Enter your text here.....'
            sx={{ marginTop: { xs: 2, lg: '16px' } }}
            minRows={4}
            multiline
            fullWidth
          />
        )}
      </Box>
      <Box
        sx={{
          marginY: { xs: 2, lg: '24px' },
          border: '1px solid #F3F3F3',
          padding: { xs: 1.5, lg: '16px' },
          borderRadius: '8px'
        }}
      >
        <Box
          display={{ xs: 'block', lg: 'flex' }}
          sx={{
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', lg: 'center' },
            gap: { xs: 2, lg: '74px' }
          }}
          flexDirection={{ xs: 'column', lg: 'row' }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant='h6' sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
              Call recordings
            </Typography>
            <Typography color='secondary' marginTop={1} sx={{ fontSize: { xs: '12px', lg: '14px' } }}>
              Toggle to record calls for playback and easy review in the logs.
            </Typography>
          </Box>
          <Box sx={{ flexShrink: 0, mt: { xs: 1, lg: 0 } }}>
            <Switch name='agent.enable_call_recording' control={control} />
          </Box>
        </Box>
      </Box>
      <Box
        mt={2}
        border='1px solid #ececec'
        borderRadius={2}
        position='relative'
        overflow='hidden'
        sx={{
          height: { xs: 160, lg: 250 }
        }}
      >
        <Box
          component='img'
          padding={-10}
          src={MultiPromptImg}
          alt='Prompt Flow'
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />

        <Button
          variant='outlined'
          onClick={() =>
            canEditBehaviour
              ? navigate(`/location/${locationId}/agent/${agentId}/configure/behavior/instructions/prompt-tree`)
              : {}
          }
          sx={{
            position: 'absolute',
            left: '50%',
            opacity: canEditBehaviour ? 1 : 0.5,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            border: 1.5,
            background: '#fff',
            zIndex: 2
          }}
        >
          Edit Conversation Flow
        </Button>
      </Box>
    </Box>
  );
}
